// InvestorSimulatorPage.js — Full page at /simulator.
// Standalone page (not inside a dashboard layout) like ReturnsEstimator.
// Layout: simulation banner, input form on left, results on right.

import React from 'react';
import { Link } from 'react-router-dom';
import { SimulatorProvider, useSimulator } from '../../context/SimulatorContext';
import SimulationBanner from './SimulationBanner';
import SimulatorInputForm from './SimulatorInputForm';
import SimulatorResults from './SimulatorResults';
import LiveSimulatorPanel from './LiveSimulatorPanel';
import RunHistory from './RunHistory';
import { Logo } from '../common/index';
import { ArrowLeft, History, Radio } from 'lucide-react';

function ModeToggle() {
  const { mode, setMode } = useSimulator();

  return (
    <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5 text-xs">
      <button
        onClick={() => setMode('historical')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
          mode === 'historical' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <History className="w-3.5 h-3.5" />
        Historical
      </button>
      <button
        onClick={() => setMode('live')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
          mode === 'live' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Radio className="w-3.5 h-3.5" />
        Start Today
      </button>
    </div>
  );
}

function SimulatorContent() {
  const { mode, loading } = useSimulator();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simulation Banner */}
      <SimulationBanner mode={mode} />

      {/* Minimal header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="small" />
            <div className="hidden sm:flex items-center gap-1.5 text-sm text-gray-400">
              <span>/</span>
              <span className="text-gray-700 font-medium">
                {mode === 'live' ? 'Live Simulator' : 'Historical Simulator'}
              </span>
            </div>
          </div>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to home</span>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">T-Bill Investment Simulator</h1>
            <p className="text-sm text-gray-500 mt-1">
              {mode === 'live'
                ? 'Start a paper trading run from today and track it as real data arrives.'
                : 'Replay historical data to see what returns would have looked like for a given investment.'}
            </p>
          </div>
          <ModeToggle />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left panel: Input form + Run History */}
          <div className="lg:col-span-4 xl:col-span-3 space-y-6">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Parameters</h2>
              <SimulatorInputForm />
            </div>

            {mode === 'historical' && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <RunHistory />
              </div>
            )}
          </div>

          {/* Right panel: Results or Live Panel */}
          <div className="lg:col-span-8 xl:col-span-9">
            <div className="bg-white rounded-xl border border-gray-100 p-5 min-h-[400px]">
              {mode === 'live' ? (
                <LiveSimulatorPanel />
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-gray-900">Results</h2>
                    {loading && (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <div className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                        Running simulation...
                      </div>
                    )}
                  </div>
                  <SimulatorResults />
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-xs text-gray-400 text-center">
            This simulation tool is for educational and illustrative purposes only and does not constitute investment advice.
            Past performance is not indicative of future results.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default function InvestorSimulatorPage() {
  return (
    <SimulatorProvider>
      <SimulatorContent />
    </SimulatorProvider>
  );
}
